import React, { useState, useEffect} from "react";
import "./assets/datamap.css";
import "leaflet/dist/leaflet.css";
import { MapContainer, TileLayer } from 'react-leaflet';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import DeviceModal from "../MarkerModals/DeviceModal/DeviceModal";
import PillarModal from "../MarkerModals/PillarModal/PillarModal";
import EditModal from "../MarkerModals/EditModal/EditModal";
import NetworkQualityModal from "../MarkerModals/NetworkQualityModal/NetworkQualityModal";
import { useMarkers } from "../../hooks/useMarkers";
import { drawStreetPolylines } from "../../Helpers/Polylines";
import ShapeDraw from './ShapeDraw';
import 'leaflet-draw/dist/leaflet.draw.css';
import LocationFinder from "./LocationFinder";
import L from "leaflet";
import { changeDimLevel } from "../../Helpers/fetchDevices";
import { DashboardMarkerLayer, DevicesMarkerLayer, ManagementMarkerLayer, RadioCoverageMarkerLayer } from "./MarkerLayers";
import {AddPointOnClick, BoundsUpdater} from "./ComponentHelpers";
import ParkingModal from "../MarkerModals/ParkingModal.js/ParkingModal";
import HoverMarker from "../HoverMarker/HoverMarker";

const calculateBoundsFromCenter = (center, zoom) => {
  const map = L.map(document.createElement('div')).setView(center, zoom);
  const bounds = map.getBounds();
  map.remove();
  return bounds;
};


const DataMap = ({ markers = [], customIcon, deviceType = null, streets, onMarkersSelected, modalFetcher, addDevice, updateDevice, deleteDevice}) => {
    const [drawingResults, setDrawingResults] = useState(null);
    const {selectedMarker, handleMarkerClick, handleCloseModal, sendDimUpdate, revertDimUpdate} = useMarkers(deviceType, modalFetcher, markers, changeDimLevel);
    const [goToPosition, setGoToPosition] = useState(false);
    const [clickedPoints, setClickedPoints] = useState([]);
    const handlePolygonCreated = (results) => {
        setDrawingResults(results);
        if (onMarkersSelected) {
        onMarkersSelected(results.markersInside);
        }
    };
    const userCoordinates = JSON.parse(localStorage.getItem("UserCoordinates"));

    const [bounds, setBounds] = useState(() => calculateBoundsFromCenter([userCoordinates.lat, userCoordinates.lng], 15));
    const [mapInstance, setMapInstance] = useState(null);

    const handleMapReady = (e) => {
        const map = e.target;
        setMapInstance(map);
        setBounds(map.getBounds());
    };

    useEffect(() => {
        setClickedPoints([]);
    }, [markers.length]);
    
    const defaultIcon = L.icon({
        iconUrl : require('../../components/DataMap/assets/marker.png'),
        iconSize: [38,38]
    });
    const stopGoToPosition = () => setGoToPosition(false);
    const addMarkerToPosition = (latlng) => {
    if (deviceType === "manage") {
        setClickedPoints(prev => [...prev, latlng]);
    }
};

    return (
        <Card>
            <CardHeader title={`Σύνολο Συσκευών: ${markers.length}`} />
            <CardContent sx={{ p: 0, height: '100%' }}>
                
                <Button onClick={() => setGoToPosition(prev => !prev)}>
                    Η τοποθεσία μου
                </Button>
                
                <MapContainer
                center={[userCoordinates.lat, userCoordinates.lng]}
                zoom={15}
                minZoom={9}
                maxZoom={20}
                whenReady={handleMapReady}
                >
                <TileLayer
                    url={`https://tiles.stadiamaps.com/tiles/outdoors/{z}/{x}/{y}{r}.png?api_key=${process.env.REACT_APP_STADIA_MAPS_API_KEY}`}
                    maxZoom={21}
                />
                <BoundsUpdater setBounds={setBounds} />


                <LocationFinder
                    goToPosition={goToPosition}
                    onAddPoint={addMarkerToPosition}
                    stopGoToPosition={stopGoToPosition}
                />
                    {(deviceType !== "radioCoverage" && deviceType!=="dashboard" && deviceType!== "manage") && 
                      <DevicesMarkerLayer markers={markers} bounds={bounds} defaultIcon={defaultIcon} handleMarkerClick={handleMarkerClick} deviceType={deviceType}/>}

                    {deviceType === "manage" && 
                      <ManagementMarkerLayer cluster={false} markers={markers} bounds={bounds} defaultIcon={defaultIcon} handleMarkerClick={handleMarkerClick} deviceType={deviceType}/>}

                    {(deviceType === "radioCoverage" && markers[0]) && 
                        <RadioCoverageMarkerLayer markers={markers} bounds={bounds} defaultIcon={defaultIcon} setClickedPoints={setClickedPoints} handleMarkerClick={handleMarkerClick} deviceType={deviceType} />
                    }

                    {deviceType === "dashboard" && <DashboardMarkerLayer/>}

                    {deviceType === "radioCoverage" && markers[0] && (
                    <AddPointOnClick
                        setClickedPoints={setClickedPoints}
                        createPointObject={(latlng) => ({
                        ...latlng,
                        radioCoverageId: markers[0].id,
                        transmission: false,
                        })}
                    />
                    )}

                    {deviceType === "manage" && markers && (
                    <AddPointOnClick
                        setClickedPoints={setClickedPoints}
                        createPointObject={(latlng) => ({
                        ...latlng,
                        })}
                    />
                    )}

                    {clickedPoints.map((pos, idx) => (
                        <HoverMarker
                            key={`marker-${idx}`}
                            marker={pos}  
                            icon={defaultIcon}
                            onClick={handleMarkerClick}
                            deviceType={deviceType}
                        />
                    ))}
                    <ShapeDraw 
                        markers={markers} 
                        onPolygonCreated={handlePolygonCreated} 
                    />
                    {deviceType === "streets" && drawStreetPolylines(streets)}
                </MapContainer>

                {deviceType === "autonomus" && (
                    <DeviceModal marker={selectedMarker} onClose={handleCloseModal} sendDimUpdate={sendDimUpdate} revertDimUpdate={revertDimUpdate}/>
                )}

                {deviceType === "networkQuality" && (
                    <NetworkQualityModal marker={selectedMarker} onClose={handleCloseModal}/>
                )}

                {deviceType === "pillarControl" && (
                    <PillarModal marker={selectedMarker} onClose={handleCloseModal} sendDimUpdate={sendDimUpdate} revertDimUpdate={revertDimUpdate}/>
                )}

                {deviceType === "manage" && (
                    <EditModal marker={selectedMarker} onClose = {handleCloseModal} addDevice={addDevice} deleteDevice={deleteDevice} updateDevice={updateDevice}/>
                )}

                {deviceType === "parking" && (
                    <ParkingModal marker={selectedMarker} onClose = {handleCloseModal} />
                )}
            </CardContent>
        </Card>
    );
};

export default DataMap;